import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/Spinner/Spinner";
import BackLink from "../../components/navigation/BackLink/BackLink";
import { UserContext } from "../../utils/contexts/UserContext";
import { hasPermission, TEMPLATE_PERMISSION_CODES } from "../../utils/permissions";
import { formatShortDate } from "../../utils/helpers/dateDisplay";
import {
  deleteTemplate,
  getActivation,
  getTemplateDeletionImpact,
  getRubricTemplates,
  resetActivation,
  setActivation,
  startDraft,
  deleteVersion,
  updateTemplate,
} from "../../services/rubricService";
import ConfirmModal from "./ConfirmModal";
import NewTemplateModal from "./NewTemplateModal";
import { LocalizedStack } from "./LocalizedFields";
import { emptyText, hasText, textFromApi, trimText } from "./templateTree";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { compareText } from "../../utils/helpers/locale";

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const STATUS_CHIPS = {
  draft: { classes: "bg-amber-100 text-amber-700", labelKey: "templates.draftChip" },
  published: { classes: "bg-emerald-100 text-emerald-700", labelKey: "templates.publishedChip" },
  retired: { classes: "bg-slate-100 text-slate-500", labelKey: "templates.retiredChip" },
};

export default function Templates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [templates, setTemplates] = useState([]);
  const [activation, setActivationState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyUuid, setBusyUuid] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, version?, template?}
  const [showNewModal, setShowNewModal] = useState(false);
  const [renaming, setRenaming] = useState(null); // template being renamed
  const [renameValue, setRenameValue] = useState(emptyText);
  // What the pending delete would take with it, resolved server-side. Null
  // while it loads — the dialog says so rather than promising less than it does.
  const [deleteImpact, setDeleteImpact] = useState(null);

  const canManage = hasPermission(user, TEMPLATE_PERMISSION_CODES.MANAGE);

  const load = useCallback(({ signal } = {}) => {
    setLoadError(false);
    return Promise.all([
      getRubricTemplates({ signal }),
      getActivation({ signal }),
    ])
      .then(([templatesRes, activationRes]) => {
        setTemplates(templatesRes?.results ?? templatesRes ?? []);
        setActivationState(activationRes);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setLoadError(true);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!canManage) return undefined;
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [canManage, load]);

  const sortedTemplates = useMemo(
    () =>
      [...templates].sort(
        (a, b) =>
          Number(b.can_edit) - Number(a.can_edit) ||
          compareText(a.name, b.name),
      ),
    [templates],
  );

  const resolvedVersion = activation?.resolved_version || null;
  const activeVersionUuid = resolvedVersion?.uuid || null;

  const openDraft = async (version) => {
    setBusyUuid(version.uuid);
    try {
      const draft = await startDraft({ uuid: version.uuid });
      navigate(`/templates/${draft.uuid}`);
    } catch (err) {
      toast.error(formatApiError(err, t("templates.editFailed")));
      setBusyUuid(null);
    }
  };

  // Toggle whether an owned template's published versions are shared down to the
  // school's child organizations (they can then Customize → fork + activate).
  const toggleShare = async (template) => {
    setBusyUuid(template.uuid);
    try {
      await updateTemplate({
        uuid: template.uuid,
        body: { shared_with_descendants: !template.shared_with_descendants },
      });
      toast.success(
        template.shared_with_descendants
          ? t("templates.unshared")
          : t("templates.shared"),
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err, t("templates.shareFailed")));
    } finally {
      setBusyUuid(null);
    }
  };

  const askDeleteTemplate = (template) => {
    setDeleteImpact(null);
    setConfirm({ type: "deleteTemplate", template });
    getTemplateDeletionImpact({ uuid: template.uuid })
      .then(setDeleteImpact)
      .catch(() => setDeleteImpact({ failed: true }));
  };

  const runConfirmAction = async () => {
    const { type, version, template } = confirm;
    setBusyUuid(version?.uuid || template?.uuid || "activation");
    try {
      if (type === "activate") {
        const next = await setActivation({ body: { template_version: version.uuid } });
        setActivationState(next);
        toast.success(t("templates.activated"));
      } else if (type === "reset") {
        const next = await resetActivation();
        setActivationState(next);
        toast.success(t("templates.resetDone"));
      } else if (type === "discard") {
        await deleteVersion({ uuid: version.uuid });
        toast.success(t("templates.discarded"));
        await load();
      } else if (type === "deleteVersion") {
        await deleteVersion({ uuid: version.uuid });
        toast.success(t("templates.versionDeleted"));
        await load();
      } else if (type === "deleteTemplate") {
        await deleteTemplate({ uuid: template.uuid });
        toast.success(t("templates.templateDeleted"));
        await load();
      } else if (type === "customize") {
        setConfirm(null);
        await openDraft(version);
        return;
      }
      setConfirm(null);
    } catch (err) {
      const fallbackKey = {
        activate: "templates.activateFailed",
        reset: "templates.resetFailed",
        discard: "templates.discardFailed",
        deleteTemplate: "templates.deleteTemplateFailed",
        deleteVersion: "templates.deleteVersionFailed",
      }[type];
      toast.error(formatApiError(err, t(fallbackKey)));
    } finally {
      setBusyUuid(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 bg-slate-50">
        <Spinner label={t("templates.loading")} className="py-24" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-5xl mx-auto py-24 text-center">
          <p className="text-sm text-rose-600 font-semibold">{t("templates.loadError")}</p>
        </div>
      </div>
    );
  }

  // Adopting a template a parent org shared down ("Use for my school"): adopt
  // as-is, read-only — distinct copy from activating an own/global version.
  const adoptShared =
    confirm?.type === "activate" &&
    !confirm.template?.can_edit &&
    !!confirm.template?.organization;

  const confirmProps = confirm && {
    activate: {
      title: adoptShared
        ? t("templates.useForMySchoolConfirmTitle", { name: confirm.template?.name })
        : t("templates.activateConfirmTitle", {
            name: confirm.template?.name,
            version: t("templates.versionN", { n: confirm.version?.version_number }),
          }),
      body: adoptShared
        ? t("templates.useForMySchoolConfirmBody", { name: confirm.template?.name })
        : activation?.applies_to_descendants
        ? t("templates.activateConfirmBodyNetwork")
        : t("templates.activateConfirmBody"),
      confirmLabel: adoptShared
        ? t("templates.useForMySchool")
        : t("templates.activate"),
      danger: false,
    },
    reset: {
      title: t("templates.resetConfirmTitle"),
      // A head office's row is its whole network's default; dropping it moves
      // every school under it, not one.
      body: t("templates.resetConfirmBody", {
        name:
          activation?.fallback_version?.template?.name ||
          resolvedVersion?.template?.name ||
          "",
      }),
      confirmLabel: t("templates.resetToDefault"),
      danger: false,
    },
    discard: {
      title: t("templates.discardConfirmTitle", {
        name: confirm.template?.name,
        version: t("templates.versionN", { n: confirm.version?.version_number }),
      }),
      body: t("templates.discardConfirmBody"),
      confirmLabel: t("templates.discardDraft"),
      danger: true,
    },
    deleteTemplate: {
      title: t("templates.deleteTemplateConfirmTitle", { name: confirm.template?.name }),
      // Names what actually disappears. Everything below is removed rather than
      // refused, and none of it is visible from the template card.
      body: [
        t("templates.deleteTemplateConfirmBody", {
          count: deleteImpact?.versions ?? 0,
        }),
        deleteImpact === null ? t("templates.deleteImpactLoading") : null,
        deleteImpact?.activations?.length
          ? t("templates.deleteImpactActivations", {
              count: deleteImpact.activations.length,
              names: deleteImpact.activations.slice(0, 3).join(", "),
            })
          : null,
        deleteImpact?.material_rules
          ? t("templates.deleteImpactMaterialRules", { count: deleteImpact.material_rules })
          : null,
        deleteImpact?.fallback_gone ? t("templates.deleteImpactFallbackGone") : null,
      ]
        .filter(Boolean)
        .join(" "),
      confirmLabel: t("templates.deleteTemplate"),
      danger: true,
    },
    deleteVersion: {
      title: t("templates.deleteVersionConfirmTitle", {
        name: confirm.template?.name,
        version: t("templates.versionN", { n: confirm.version?.version_number }),
      }),
      // Two consequences the row itself doesn't show: the template goes with
      // the last version, and a school sitting on this version falls back.
      body: [
        t("templates.deleteVersionConfirmBody"),
        (confirm.template?.versions?.length || 0) <= 1
          ? t("templates.deleteVersionLastOne", { name: confirm.template?.name })
          : null,
        confirm.version?.uuid === activeVersionUuid
          ? t("templates.deleteVersionActive")
          : null,
      ]
        .filter(Boolean)
        .join(" "),
      confirmLabel: t("templates.deleteVersion"),
      danger: true,
    },
    customize: {
      title: t("templates.customizeConfirmTitle", { name: confirm.template?.name }),
      // A template owned by another org (a parent shared down) names its owner;
      // a global/system template uses the generic wording.
      body: confirm.template?.organization
        ? t("templates.customizeConfirmBodyShared", {
            name: confirm.template?.name,
            owner: confirm.template?.organization_name,
          })
        : t("templates.customizeConfirmBody", {
            name: confirm.template?.name,
            version: t("templates.versionN", { n: confirm.version?.version_number }),
          }),
      confirmLabel: t("templates.customize"),
      danger: false,
    },
  }[confirm.type];

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <BackLink />
        {/* Header */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg shadow-indigo-900/10 hover:bg-indigo-700 transition shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            {t("templates.newTemplate")}
          </button>
        </div>

        {/* Active rubric hero */}
        <div className="bg-slate-900 rounded-2xl shadow-md p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                  {t("templates.activeRubricTitle")}
                </p>
                {resolvedVersion ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <h2 className="text-lg font-bold truncate">
                        {resolvedVersion.template?.name}
                      </h2>
                      <span className="text-sm font-semibold text-slate-400">
                        {t("templates.versionN", { n: resolvedVersion.version_number })}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                          activation?.explicit
                            ? "bg-indigo-500/30 text-indigo-300"
                            : "bg-slate-700 text-slate-300"
                        }`}
                      >
                        {activation?.explicit
                          ? t("templates.schoolChoiceChip")
                          : activation?.inherited_from
                          ? t("templates.networkDefaultChip", {
                              org: activation.inherited_from.name,
                            })
                          : t("templates.systemDefaultChip")}
                      </span>
                    </div>
                    {resolvedVersion.published_at && (
                      <p className="text-xs text-slate-400 mt-1">
                        {t("templates.publishedOn", {
                          date: formatShortDate(resolvedVersion.published_at),
                        })}
                      </p>
                    )}
                  </>
                ) : (
                  <h2 className="text-lg font-bold mt-1">{t("templates.noActiveRubric")}</h2>
                )}
                <p className="text-xs text-slate-400 mt-2 max-w-xl leading-relaxed">
                  {t("templates.activeRubricHint")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 self-start sm:self-center">
              {resolvedVersion && (
                <button
                  type="button"
                  onClick={() => navigate(`/templates/${resolvedVersion.uuid}`)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-xs font-bold text-slate-200 hover:bg-slate-700 hover:text-white transition"
                >
                  {t("templates.viewActive")}
                </button>
              )}
              {activation?.explicit && (
                <button
                  type="button"
                  onClick={() => setConfirm({ type: "reset" })}
                  className="px-4 py-2 rounded-xl border border-slate-700 text-xs font-bold text-slate-300 hover:bg-slate-800 hover:text-white transition"
                >
                  {t("templates.resetToDefault")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Template cards */}
        {sortedTemplates.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center text-sm text-slate-400 font-semibold">
            {t("templates.emptyTemplates")}
          </div>
        ) : (
          sortedTemplates.map((template) => (
            <div
              key={template.uuid}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-slate-900 truncate">
                      {template.name}
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                        template.can_edit
                          ? "bg-indigo-100 text-indigo-700"
                          : template.organization
                            ? "bg-amber-100 text-amber-700"
                            : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {template.can_edit
                        ? t("templates.mySchoolChip")
                        : template.organization
                          ? t("templates.sharedChip")
                          : t("templates.systemChip")}
                    </span>
                    {template.can_edit && (
                      <>
                        <button
                          type="button"
                          title={t("templates.rename")}
                          aria-label={t("templates.rename")}
                          onClick={() => {
                            setRenameValue(textFromApi(template, "name"));
                            setRenaming(template);
                          }}
                          className="p-1 rounded-lg text-slate-300 hover:text-slate-700 hover:bg-slate-100 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          disabled={busyUuid === template.uuid}
                          onClick={() => toggleShare(template)}
                          title={t("templates.shareHint")}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition disabled:opacity-50 ${
                            template.shared_with_descendants
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          {template.shared_with_descendants
                            ? t("templates.sharedWithSchools")
                            : t("templates.shareWithSchools")}
                        </button>
                      </>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
                {/* An icon, as asked — but rose rather than slate-300 and a
                    size up: at 14px in the same grey as the chips beside it,
                    nobody could find it. Disabled rather than hidden, with the
                    reason in the tooltip. */}
                {template.can_edit && (
                  <button
                    type="button"
                    disabled={busyUuid === template.uuid || !template.can_delete}
                    onClick={() => askDeleteTemplate(template)}
                    title={
                      template.can_delete
                        ? t("templates.deleteTemplate")
                        : t("templates.deleteBlockedObservations")
                    }
                    aria-label={t("templates.deleteTemplate")}
                    className="shrink-0 p-2 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>

              <div className="divide-y divide-slate-50">
                {(template.versions || []).map((version) => {
                  const isActive = version.uuid === activeVersionUuid;
                  const isBusy = busyUuid === version.uuid;
                  return (
                    <div
                      key={version.uuid}
                      className="px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-black text-slate-700 w-10">
                          {t("templates.versionN", { n: version.version_number })}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${(STATUS_CHIPS[version.status] || STATUS_CHIPS.retired).classes}`}
                        >
                          {t((STATUS_CHIPS[version.status] || STATUS_CHIPS.retired).labelKey)}
                        </span>
                        {isActive && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                            {t("templates.activeBadge")}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">
                          {version.published_at
                            ? t("templates.publishedOn", {
                                date: formatShortDate(version.published_at),
                              })
                            : version.status === "draft"
                            ? t("templates.draftInvisibleHint")
                            : t("templates.notPublishedYet")}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap shrink-0">
                        {/* A draft is only actionable inside our own write
                            scope. A head-org manager can READ a child school's
                            draft, and offering Continue editing / Discard there
                            just walks them into a 403 with their work typed in. */}
                        {version.status === "draft" && template.can_edit ? (
                          <>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => navigate(`/templates/${version.uuid}`)}
                              className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 transition disabled:opacity-60"
                            >
                              {t("templates.continueEditing")}
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirm({ type: "discard", version, template })}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-50 transition disabled:opacity-60"
                            >
                              {t("templates.discardDraft")}
                            </button>
                          </>
                        ) : (
                          <>
                            {version.status === "published" && !isActive && (
                              <button
                                type="button"
                                disabled={isBusy}
                                onClick={() => setConfirm({ type: "activate", version, template })}
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition disabled:opacity-60"
                              >
                                {/* A template shared down from a parent org is
                                    adopted as-is ("Use for my school"); own /
                                    global versions read as plain "Activate". */}
                                {!template.can_edit && template.organization
                                  ? t("templates.useForMySchool")
                                  : t("templates.activate")}
                              </button>
                            )}
                            {/* Same destination, honest label: the editor opens
                                in whichever mode the server allows — full while
                                nothing has been observed against the version,
                                text-only once something has. */}
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => navigate(`/templates/${version.uuid}`)}
                              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:border-slate-400 transition disabled:opacity-60"
                            >
                              {version.editing_mode === "full"
                                ? t("templates.edit")
                                : version.editing_mode === "wording"
                                  ? t("templates.correctWording")
                                  : t("templates.view")}
                            </button>
                            {version.status === "published" &&
                              (template.can_edit ? (
                                <button
                                  type="button"
                                  disabled={isBusy}
                                  onClick={() => openDraft(version)}
                                  className="px-3 py-1.5 rounded-lg border border-indigo-200 text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition disabled:opacity-60"
                                >
                                  {t("templates.newVersion")}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={isBusy}
                                  onClick={() => setConfirm({ type: "customize", version, template })}
                                  className="px-3 py-1.5 rounded-lg border border-indigo-200 text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition disabled:opacity-60"
                                >
                                  {t("templates.customize")}
                                </button>
                              ))}
                            {version.status === "published" && template.can_edit && (
                              <button
                                type="button"
                                disabled={isBusy || version.is_in_use}
                                onClick={() =>
                                  setConfirm({ type: "deleteVersion", version, template })
                                }
                                title={
                                  version.is_in_use
                                    ? t("templates.deleteVersionBlocked")
                                    : t("templates.deleteVersion")
                                }
                                aria-label={t("templates.deleteVersion")}
                                className="p-2 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                              >
                                <TrashIcon />
                              </button>
                            )}

                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {confirm && confirmProps && (
        <ConfirmModal
          {...confirmProps}
          busy={busyUuid !== null}
          onConfirm={runConfirmAction}
          onClose={() => setConfirm(null)}
        />
      )}

      {showNewModal && (
        <NewTemplateModal
          templates={templates}
          onClose={() => setShowNewModal(false)}
          onCreated={(draft) => navigate(`/templates/${draft.uuid}`)}
        />
      )}

      {renaming && (
        <ConfirmModal
          title={t("templates.renameTitle")}
          confirmLabel={t("common.save")}
          busy={busyUuid === renaming.uuid}
          onConfirm={async () => {
            if (!hasText(renameValue)) return;
            setBusyUuid(renaming.uuid);
            try {
              await updateTemplate({
                uuid: renaming.uuid,
                body: { name: trimText(renameValue) },
              });
              toast.success(t("templates.renameSaved"));
              setRenaming(null);
              await load();
            } catch (err) {
              toast.error(formatApiError(err, t("templates.renameFailed")));
            } finally {
              setBusyUuid(null);
            }
          }}
          onClose={() => setRenaming(null)}
        >
          <div className="mt-4">
            <LocalizedStack
              label={t("templates.nameLabel")}
              value={renameValue}
              autoFocus
              placeholder={t("templates.namePlaceholder")}
              onChange={setRenameValue}
            />
          </div>
        </ConfirmModal>
      )}
    </div>
  );
}
